import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { useAuth } from './AuthorizationProvider';
import { ETL_INTEGRATION_TYPE, Values } from '../components/configuration/types';
import { PATH_MY_CONFIGURATIONS } from '../router/routes';
import { isIntegrationTypeSupported } from '../scene/integrations/config';
import { ETL_PERMISSIONS } from '../utils/keycloakUtils';

interface IntegrationsProviderType {
  isInEditMode: boolean;
  isInReadOnlyMode: boolean;
  canDeleteConfiguration: boolean;
  integrationType: Values<typeof ETL_INTEGRATION_TYPE>;
  integrationId?: number;
}

export const integrationsProviderMock: IntegrationsProviderType = {
  isInEditMode: false,
  isInReadOnlyMode: false,
  canDeleteConfiguration: false,
  integrationId: undefined,
  integrationType: ETL_INTEGRATION_TYPE.wxcc,
};

const IntegrationsContext = React.createContext(integrationsProviderMock);

export const IntegrationsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { search } = useLocation();
  const { integrationId, integrationType } = useParams();
  const { userHasPermissions } = useAuth();

  const integrationIdNumber = Number(integrationId);
  const isIntegrationIdValidNumber = !Number.isNaN(integrationIdNumber);
  const isInReadOnlyMode = !userHasPermissions([ETL_PERMISSIONS.updateConfiguration]) && isIntegrationIdValidNumber;
  const canDeleteConfiguration =
    userHasPermissions([ETL_PERMISSIONS.deleteConfiguration]) && isIntegrationIdValidNumber;

  const supportedIntegrationType = isIntegrationTypeSupported(integrationType);

  if (supportedIntegrationType === ETL_INTEGRATION_TYPE.unknown) {
    return <Navigate to={`/${PATH_MY_CONFIGURATIONS}${search}`} />;
  }

  const value = {
    isInEditMode: isIntegrationIdValidNumber,
    isInReadOnlyMode,
    canDeleteConfiguration,
    integrationId: isIntegrationIdValidNumber ? integrationIdNumber : undefined,
    integrationType: supportedIntegrationType,
  };

  return <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>;
};

export const IntegrationsConsumer = IntegrationsContext.Consumer;

export const useIntegrations = () => React.useContext(IntegrationsContext);
